import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { removeLaptopFromStockRpc } from '@/services/laptops';
import { queryKeys } from '@/lib/queryKeys';
import { parseDatabaseError } from '@/lib/errors';
import { Laptop } from '@/types/models';
import { Modal } from '@/components/common/Modal';
import { ErrorAlert } from '@/components/common/ErrorAlert';
import { Trash2, AlertTriangle, Loader2, Laptop as LaptopIcon } from 'lucide-react';

interface RemoveLaptopModalProps {
  laptop: Laptop;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const PRESET_REASONS = [
  'Entered by mistake / Test record',
  'Damaged beyond use / Hardware failure',
  'Lost or stolen inventory',
  'Duplicate inventory entry',
  'Physical stock count correction',
  'Other administrative reason',
];

export const RemoveLaptopModal: React.FC<RemoveLaptopModalProps> = ({
  laptop,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const queryClient = useQueryClient();
  const [preset, setPreset] = useState<string>(PRESET_REASONS[0]);
  const [customReason, setCustomReason] = useState<string>('');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const finalReason = customReason.trim()
    ? `${preset}: ${customReason.trim()}`
    : preset;

  const mutation = useMutation({
    mutationFn: async () => {
      if (!finalReason.trim()) {
        throw new Error('A reason is required to remove this laptop from stock.');
      }
      return await removeLaptopFromStockRpc({
        laptopId: laptop.id,
        reason: finalReason.trim(),
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.laptops.all });
      onClose();
      if (onSuccess) onSuccess();
    },
    onError: (err: unknown) => {
      setErrorMsg(parseDatabaseError(err));
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg(null);
    mutation.mutate();
  };

  return (
    <Modal
      isOpen={isOpen}
      title={`Remove Laptop from Stock — ${laptop.stock_id}`}
      onClose={onClose}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {errorMsg && <ErrorAlert message={errorMsg} />}

        {/* Warning Banner */}
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-2 text-xs text-red-900">
          <AlertTriangle className="w-4 h-4 text-brand-red shrink-0 mt-0.5" />
          <div className="space-y-1">
            <p className="font-bold">Administrative Stock Removal</p>
            <p className="text-[11px] text-red-800 leading-relaxed">
              This action will mark the laptop as <strong>scrapped</strong>, remove it from active and public catalogues, and log a permanent <strong>-1</strong> stock movement in the audit trail.
            </p>
          </div>
        </div>

        {/* Laptop Summary Card */}
        <div className="p-3 bg-brand-bg rounded-lg border border-brand-border space-y-2 text-xs">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <span className="p-1.5 bg-white border border-brand-border rounded text-brand-black">
                <LaptopIcon className="w-4 h-4" />
              </span>
              <div>
                <span className="px-2 py-0.5 bg-brand-black text-white text-[10px] font-bold rounded mr-1.5">
                  {laptop.stock_id}
                </span>
                <strong className="text-brand-black">{laptop.brand} {laptop.model}</strong>
              </div>
            </div>
            <span className="px-2 py-0.5 bg-green-100 text-green-800 font-semibold rounded text-[10px] uppercase">
              {laptop.status}
            </span>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 pt-2 border-t border-brand-border text-[11px]">
            <div>
              <span className="text-brand-muted block">Serial Number</span>
              <span className="font-mono text-brand-black">{laptop.serial_number || 'N/A'}</span>
            </div>
            {laptop.service_tag && (
              <div>
                <span className="text-brand-muted block">Service Tag</span>
                <span className="font-mono text-brand-black">{laptop.service_tag}</span>
              </div>
            )}
            <div>
              <span className="text-brand-muted block">Condition</span>
              <span className="capitalize text-brand-black">{laptop.condition}</span>
            </div>
          </div>
        </div>

        {/* Reason Presets */}
        <div>
          <label className="block text-xs font-semibold text-brand-black uppercase tracking-wider mb-1">
            Removal Category <span className="text-brand-red">*</span>
          </label>
          <select
            value={preset}
            onChange={(e) => setPreset(e.target.value)}
            className="w-full px-3 py-2 bg-brand-bg border border-brand-border rounded-md text-xs font-semibold text-brand-black focus:ring-2 focus:ring-brand-green"
          >
            {PRESET_REASONS.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </div>

        {/* Detailed Reason Notes */}
        <div>
          <label className="block text-xs font-semibold text-brand-black uppercase tracking-wider mb-1">
            Reason Details / Explanation <span className="text-brand-red">*</span>
          </label>
          <textarea
            rows={2}
            placeholder="Provide specific context or justification for this stock removal..."
            value={customReason}
            onChange={(e) => setCustomReason(e.target.value)}
            className="w-full px-3 py-2 bg-brand-bg border border-brand-border rounded-md text-xs text-brand-black focus:ring-2 focus:ring-brand-green"
            required
          />
          <p className="text-[11px] text-brand-muted mt-1">
            This reason is recorded in the permanent stock movements audit ledger.
          </p>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end space-x-3 pt-3 border-t border-brand-border">
          <button
            type="button"
            onClick={onClose}
            disabled={mutation.isPending}
            className="px-4 py-2 text-xs font-medium text-brand-muted hover:text-brand-black transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={mutation.isPending || !finalReason.trim()}
            className="inline-flex items-center space-x-1.5 px-4 py-2 bg-brand-red text-white text-xs font-bold rounded-md hover:bg-red-700 transition-colors disabled:opacity-50 shadow-sm"
          >
            {mutation.isPending ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Trash2 className="w-3.5 h-3.5" />
            )}
            <span>Remove Laptop from Stock</span>
          </button>
        </div>
      </form>
    </Modal>
  );
};
